import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const VIDEO_SOURCE = '/Oakley Kylian Mbappé video.mp4';
const VIDEO_POSTER = '/hero.webp';
const VIDEO_PRELOAD_MARGIN = '320px 0px';
const VIDEO_PLAYBACK_THRESHOLD = 0.5;
const MOBILE_BREAKPOINT_PX = 768;
const MOBILE_HEIGHT_REDUCTION_PX = 10;

const CatalogLifestyleVideoSection = () => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const [referenceHeight, setReferenceHeight] = useState(null);

  useEffect(() => {
    const sectionElement = sectionRef.current;

    if (!sectionElement || typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') {
      setShouldLoadVideo(true);
      return undefined;
    }

    const preloadObserver = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoadVideo(true);
            preloadObserver.disconnect();
          }
        });
      },
      {
        rootMargin: VIDEO_PRELOAD_MARGIN,
        threshold: 0.01,
      }
    );

    preloadObserver.observe(sectionElement);

    return () => preloadObserver.disconnect();
  }, []);

  useEffect(() => {
    const sectionElement = sectionRef.current;

    if (!sectionElement || typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') {
      setIsVideoVisible(true);
      return undefined;
    }

    const playbackObserver = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVideoVisible(entry.intersectionRatio >= VIDEO_PLAYBACK_THRESHOLD);
        });
      },
      {
        threshold: [0, VIDEO_PLAYBACK_THRESHOLD, 1],
      }
    );

    playbackObserver.observe(sectionElement);

    return () => playbackObserver.disconnect();
  }, []);

  useEffect(() => {
    const videoElement = videoRef.current;

    if (!videoElement || !shouldLoadVideo) {
      return undefined;
    }

    if (isVideoVisible) {
      const playPromise = videoElement.play();
      if (typeof playPromise?.catch === 'function') {
        playPromise.catch(() => {});
      }
      return undefined;
    }

    videoElement.pause();
    return undefined;
  }, [isVideoVisible, shouldLoadVideo]);

  useEffect(() => () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const referenceElement = document.querySelector('[data-catalog-attention-reference="true"]');

    if (!referenceElement) {
      return undefined;
    }

    const syncReferenceHeight = () => {
      const nextHeight = referenceElement.getBoundingClientRect().height;

      if (nextHeight > 0) {
        const adjustedHeight = window.innerWidth < MOBILE_BREAKPOINT_PX
          ? Math.max(nextHeight - MOBILE_HEIGHT_REDUCTION_PX, 0)
          : nextHeight;

        setReferenceHeight(adjustedHeight);
      }
    };

    syncReferenceHeight();

    if (typeof window.ResizeObserver !== 'function') {
      window.addEventListener('resize', syncReferenceHeight);
      return () => window.removeEventListener('resize', syncReferenceHeight);
    }

    const resizeObserver = new window.ResizeObserver(() => {
      syncReferenceHeight();
    });

    resizeObserver.observe(referenceElement);
    window.addEventListener('resize', syncReferenceHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncReferenceHeight);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden rounded-[24px] bg-black shadow-product-normal"
      style={referenceHeight ? { height: `${referenceHeight}px` } : undefined}
    >
      <div className="h-full w-full bg-black">
        {shouldLoadVideo ? (
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            muted
            loop
            playsInline
            preload="none"
            poster={VIDEO_POSTER}
          >
            <source src={VIDEO_SOURCE} type="video/mp4" />
          </video>
        ) : (
          <img
            src={VIDEO_POSTER}
            alt="Oakley lifestyle preview"
            className="h-full w-full object-cover"
            loading="lazy"
          />
        )}
      </div>

      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.66)_0%,rgba(0,0,0,0.36)_42%,rgba(0,0,0,0.18)_100%)]" />

      <div className="absolute inset-0 flex items-center text-white">
        <div className="w-[58%] max-w-[220px] px-[16px] py-[14px] md:w-auto md:max-w-none md:px-[28px] md:py-[28px]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/70 md:text-[11px]">
            Lifestyle film
          </p>
          <h2 className="mt-[6px] max-w-[180px] text-[18px] font-bold leading-[1.05] tracking-tight text-white md:mt-[10px] md:max-w-[520px] md:text-[42px]">
            Experience the lifestyle
          </h2>
          <p className="mt-[6px] max-w-[190px] text-[11px] leading-[1.35] text-white/80 md:mt-[10px] md:max-w-[420px] md:text-[16px] md:leading-[1.6]">
            Movement, confidence, and performance-led styling woven directly into the shopping journey.
          </p>
          <Link
            to="/catalog"
            className="mt-[10px] inline-flex items-center justify-center rounded-[12px] bg-white px-[14px] py-[8px] text-[12px] font-semibold text-black transition hover:bg-white/90 md:mt-[18px] md:px-[18px] md:py-[12px] md:text-[14px]"
          >
            Shop Now
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CatalogLifestyleVideoSection;
